import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import DefaultAccordion from "@/features/Common/components/DefaultAccordion";
import FullscreenBackground from "@/features/Common/components/FullscreenBackground";
import IconLoader from "@/features/Common/components/IconLoader";
import InfoContextPanel from "@/features/Common/components/InfoContextPanel";
import StepsCounter from "@/features/Common/components/StepsCounter";
import WatermarkIcon from "@/features/Common/components/WatermarkIcon";
import { trackPageViewV2 } from "@/services/analytics-adapter";
import {
  PathologyPanel,
  VoucherVendorDefinitionId,
  isDoctorConsultType,
} from "@/services/core-api-adapter";
import theme from "@/theme";
import { convertPxToRem, mapErrorToMessage } from "@/utils";
import { Box, Button, InputLabel, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import MedicalConsultationPartnerMoreInfoModal from "../../../MedicalConsultations/components/MedicalConsultationPartnerMoreInfoModal";
import VoucherInputField from "../VoucherInputField";

interface BuyVoucherPromptProps {
  voucherVendorDefinitionId: string;
  onContinueButtonClick: (voucherCode?: string) => void;
  onBackButtonClick: () => void;
  selectedBloodPanel?: PathologyPanel | null;
  applyVoucherError: any;
  voucherCode: string | undefined;
}

export function BuyVoucherPrompt({
  voucherVendorDefinitionId,
  onContinueButtonClick,
  onBackButtonClick,
  selectedBloodPanel,
  applyVoucherError,
  voucherCode: voucherCodeFromContext,
}: BuyVoucherPromptProps) {
  const { t } = useTranslation();
  const [localErrorMessage, setLocalErrorMessage] = useState("");
  const [hasError, setHasError] = useState(Boolean(applyVoucherError));
  const errorMessage = mapErrorToMessage(applyVoucherError, t);
  const [error, setError] = useState(errorMessage.length > 0 ? true : false);
  const [voucherCode, setVoucherCode] = useState(voucherCodeFromContext || "");

  const voucherCodeRegex = /^[A-Z]{4}-[0-9]{4}-[A-Z]{4}$/;

  const [transformedVoucherSubType, setTransformedVoucherSubType] =
    useState<string>("");

  function transformVoucherSubTypeForTranslationKeys(
    voucherVendorDefinitionId: string
  ) {
    if (voucherVendorDefinitionId === VoucherVendorDefinitionId.NURSE_CONSULT) {
      return setTransformedVoucherSubType("NurseVoucher");
    } else if (isDoctorConsultType(voucherVendorDefinitionId)) {
      return setTransformedVoucherSubType("DoctorVoucher");
    } else if (
      voucherVendorDefinitionId ===
      VoucherVendorDefinitionId.DOCTOR_MEDICINE_CONSULT
    ) {
      return setTransformedVoucherSubType("DoctorMedicineVoucher");
    } else {
      return setTransformedVoucherSubType("BloodTestVoucher");
    }
  }

  const formatVoucherCode = (value: string) => {
    const cleanedValue = value.replace(/[^A-Z0-9-]/gi, "").toUpperCase();

    if (cleanedValue.length === 4 && !cleanedValue.includes("-")) {
      return cleanedValue + "-";
    } else if (
      cleanedValue.length === 9 &&
      cleanedValue.indexOf("-") === 4 &&
      cleanedValue.lastIndexOf("-") === 4
    ) {
      return cleanedValue + "-";
    }

    return cleanedValue;
  };

  const handleVoucherCodeChange = (code: string) => {
    setVoucherCode(code);
    if (applyVoucherError) {
      setHasError(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const isDeleting = voucherCode.length > value.length;

    if (
      isDeleting &&
      voucherCode.endsWith("-") &&
      value === voucherCode.slice(0, -1)
    ) {
      const newValue = voucherCode.slice(0, -2);
      setVoucherCode(newValue);
      handleVoucherCodeChange(newValue);
      return;
    }

    const formattedValue = formatVoucherCode(value);

    if (voucherCodeRegex.test(formattedValue)) {
      setError(false);
      setLocalErrorMessage("");

      setVoucherCode(formattedValue);
      handleVoucherCodeChange(formattedValue);
    } else {
      setError(true);
      setLocalErrorMessage(
        t("AddOrUseVoucher.Voucher.UseVoucherTab.errorMessage.generic")
      );

      setVoucherCode(formattedValue);
      handleVoucherCodeChange(formattedValue);
    }
  };

  function handleOnContinueButtonClick() {
    if (voucherCode !== "") {
      onContinueButtonClick(voucherCode);
      console.log("Voucher code:", voucherCode);
    } else {
      onContinueButtonClick();
    }
  }

  useEffect(() => {
    transformVoucherSubTypeForTranslationKeys(voucherVendorDefinitionId);
  }, [voucherVendorDefinitionId]);

  useEffect(() => {
    trackPageViewV2({
      pageName: `${transformedVoucherSubType} prompt`,
      pageSubSection1: transformedVoucherSubType,
      pageSubSection2: `${transformedVoucherSubType}: Next steps`,
      pageCategory: "Vouchers",
    });
  }, []);

  function onInfoIconClick() {
    setIsInfoModalOpen(true);
  }

  function onMoreInfoModalClose() {
    setIsInfoModalOpen(false);
  }

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  return (
    <>
      <Stack p={2} justifyContent="space-between" flexGrow={1} minHeight="100%">
        <Stack
          color="neutral.50"
          sx={{
            height: "100%",
          }}
        >
          <Stack spacing={3}>
            {selectedBloodPanel ? (
              <Typography variant="h1" color="info.main" textAlign="center">
                {t("PathologyPrompt.title")}
              </Typography>
            ) : (
              <Stack
                direction="row"
                alignSelf="center"
                alignItems="center"
                spacing={2.5}
                p={2}
              >
                <WatermarkIcon
                  height={36}
                  width={36}
                  color={"neutral.50"}
                  foreground="neutral.50"
                  Icon={"FaceIconBackground"}
                  iconSize="large"
                />

                <IconLoader
                  icon="ArrowNextIcon"
                  sx={{
                    fontSize: convertPxToRem(25),
                    alignSelf: "center",
                    color: "neutral.50",
                  }}
                />
                <Box
                  src={`/assets/MedicalAssistancePartners/Intercare/logo.svg`}
                  component="img"
                  sx={{
                    zIndex: 2,
                  }}
                  alt={t("VoucherFlow.screens.animationAlternateText")}
                />

                <ButtonWithAnalytics
                  page="Voucher prompt"
                  text={t("MedicalConsultationPartner.more.info.modal.title")}
                  intent="informational"
                  variant="text"
                  aria-label={t(
                    "MedicalConsultationPartner.more.info.modal.title"
                  )}
                >
                  <IconLoader
                    icon="InformationSymbolInCircleIcon"
                    color="info"
                    onClick={onInfoIconClick}
                    sx={{ fontSize: convertPxToRem(20), cursor: "pointer" }}
                  />
                </ButtonWithAnalytics>
              </Stack>
            )}

            <InfoContextPanel
              title={t("InfoContextPanel.vouchers.title")}
              icon="CloudStatusIcon"
              text={t(
                `InfoContextPanel.vouchers.${transformedVoucherSubType}.subtitle` as any
              )}
            />

            <Stack
              bgcolor="neutral.50"
              borderRadius={theme.shape.borderRadius}
              p={4}
              spacing={2}
            >
              <Trans>
                <Typography variant="h2">
                  {t(`${transformedVoucherSubType}.stepsPanel.title` as any)}
                </Typography>
              </Trans>

              <Stack direction="row" alignItems="center">
                <StepsCounter stepCounter={1} />
                <Stack sx={{ width: "100%" }}>
                  {selectedBloodPanel !== null ? (
                    <DefaultAccordion
                      title={t(
                        `${transformedVoucherSubType}.stepsPanel.step1Label` as any,
                        {
                          price: selectedBloodPanel?.amountTotalFormatted || "",
                        }
                      )}
                      aria-label={t(
                        `${transformedVoucherSubType}.stepsPanel.step1Label` as any
                      )}
                      key={t(
                        `${transformedVoucherSubType}.stepsPanel.step1Label` as any
                      )}
                      accordionStyles={{
                        textTransform: "capitalize",
                        boxShadow: "none",
                        width: "100%",
                        padding: "0",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between">
                        <Typography>
                          {t("BuyVoucherPrompt.priceBreakdown.AmpathFee")}
                        </Typography>
                        <Typography color="primary">
                          {selectedBloodPanel?.panelTestsAmountFormatted}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography>
                          {t("BuyVoucherPrompt.priceBreakdown.UnuPlatformFee")}
                        </Typography>
                        <Typography color="primary">
                          {selectedBloodPanel?.unuHandlingFeeAmountFormatted}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography>
                          {t(
                            "BuyVoucherPrompt.priceBreakdown.UnuTransactionFee"
                          )}
                        </Typography>
                        <Typography color="primary">
                          {
                            selectedBloodPanel?.unuTransactionalFeeAmountFormatted
                          }
                        </Typography>
                      </Stack>
                    </DefaultAccordion>
                  ) : (
                    <Typography ml={2} variant="body1">
                      <Trans
                        i18nKey={
                          `${transformedVoucherSubType}.stepsPanel.step1Label` as any
                        }
                      ></Trans>
                    </Typography>
                  )}
                </Stack>
              </Stack>

              <Stack direction="row">
                <StepsCounter stepCounter={2} />

                <Typography ml={2} variant="body1">
                  <Trans
                    i18nKey={
                      `${transformedVoucherSubType}.stepsPanel.step2Label` as any
                    }
                  ></Trans>
                </Typography>
              </Stack>

              <Stack direction="row">
                <StepsCounter stepCounter={3} />
                <Stack>
                  <Typography ml={2} variant="body1">
                    <Trans
                      i18nKey={
                        `${transformedVoucherSubType}.stepsPanel.step3Label` as any
                      }
                    ></Trans>
                  </Typography>

                  {transformedVoucherSubType !== "BloodTestVoucher" && (
                    <Typography ml={2} variant="body2">
                      <Trans
                        i18nKey={
                          `${transformedVoucherSubType}.stepsPanel.disclaimer` as any
                        }
                      ></Trans>
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Stack>

            <Stack
              bgcolor="neutral.50"
              borderRadius={theme.shape.borderRadius}
              p={4}
              spacing={2}
            >
              <Stack spacing={1}>
                <Typography variant="h2">
                  {t("BuyVoucherPrompt.applyVoucherCode.title")}
                </Typography>
                <Typography variant="body1">
                  {t("BuyVoucherPrompt.applyVoucherCode.subtitle")}
                </Typography>
              </Stack>
              <Stack>
                <InputLabel>
                  <Typography
                    textAlign="left"
                    variant="body2"
                    color={hasError ? "error" : "neutral.main"}
                  >
                    {t("BuyVoucherPrompt.applyVoucherCode.inputLabel")}
                  </Typography>
                </InputLabel>
                <VoucherInputField
                  handleInputChange={handleInputChange}
                  localErrorMessage={localErrorMessage}
                  error={error}
                  errorMessage={errorMessage}
                  setLocalErrorMessage={setLocalErrorMessage}
                  setError={setError}
                  voucherCode={voucherCode}
                />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                onClick={onBackButtonClick}
                color="info"
                fullWidth
                variant="outlined"
              >
                {t("common.backButton")}
              </Button>
              <Button
                onClick={handleOnContinueButtonClick}
                color="info"
                fullWidth
              >
                {t("common.continueButton")}
              </Button>
            </Stack>
          </Stack>
        </Stack>
        <FullscreenBackground color="primary.main" />
      </Stack>
      <MedicalConsultationPartnerMoreInfoModal
        isOpen={isInfoModalOpen}
        onClose={onMoreInfoModalClose}
      />
    </>
  );
}
